import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";

import * as tokens from "../../design-tokens";
import { SvgIcon, SvgNames } from "../../assets/icons/SvgIcon";
import { Button } from "../Button";
import { useScreenSizes } from "../../hooks/useSizes";
import { Skeleton } from "../../components/Skeleton";
import { DateHeader } from "./DateHeader";
import { dummyLoadingData } from "./fixtures";

const TEST_ID = "experience-ui-components--TimeScheduler--TimeSlotButton";
const testIdMap = {
  primary: "selected",
  secondary: "available",
};

function groupByDate(appointments: Appointment[]): GroupedAppointments {
  if (!appointments) {
    throw new Error("appointments cannot be undefined");
  }

  return appointments.reduce((acc, curr) => {
    const date = new Date(curr.startTime);
    const localeDateString = date.toISOString().split("T")[0];

    if (!acc[localeDateString]) {
      acc[localeDateString] = [];
    }

    acc[localeDateString].push(curr);
    return acc;
  }, {});
}

function getFormattedTime(strDate: string, timeZone?: string): string {
  return new Date(strDate).toLocaleTimeString("en-us", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  });
}

function paginate(
  groupedAppointments: GroupedAppointments,
  currentPage: number,
  itemsPerPage: number
) {
  if (currentPage < 1 || itemsPerPage < 1) {
    throw new Error("Invalid page number or max dates per page.");
  }

  const dates = Object.keys(groupedAppointments);
  const totalDates = dates.length;

  const maxPages = Math.ceil(totalDates / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedDates = dates.slice(startIndex, endIndex);

  const paginatedAppointments: GroupedAppointments = {};
  paginatedDates.forEach((date) => {
    paginatedAppointments[date] = groupedAppointments[date];
  });

  return { paginatedAppointments, paginatedDates, maxPages };
}

const SingleTimeSlot = ({
  appointment,
  selectedAppointment,
  timeZone,
  isLoading,
  onAppointmentSelected,
}: SingleTimeSlotProps) => {
  if (isLoading) {
    return <StyledSkeleton />;
  }

  const formattedTime = `${getFormattedTime(
    appointment.startTime,
    timeZone
  )} - ${getFormattedTime(appointment.endTime, timeZone)}`;

  const isSelected =
    selectedAppointment?.startTime === appointment.startTime &&
    selectedAppointment?.endTime === appointment.endTime;

  const handleSelection = (appointment: Appointment) => {
    if (
      selectedAppointment?.startTime === appointment.startTime &&
      selectedAppointment?.endTime === appointment.endTime
    ) {
      return onAppointmentSelected(null);
    }

    onAppointmentSelected(appointment);
  };

  const color = isSelected ? "primary" : "secondary";

  return (
    <TimeSlotButton
      data-testid={`${TEST_ID}${
        appointment.available ? `--${testIdMap[color]}` : ""
      }`}
      disabled={appointment?.available === false}
      color={color}
      onClick={() => handleSelection(appointment)}
      aria-label={`time-slot-button-${appointment.startTime}`}
      iconRight={
        isSelected ? (
          <SvgIcon
            style={{ marginLeft: -24 }}
            name={SvgNames.CheckFilled}
            width={24}
            height={24}
            color={tokens.SUCCESS_60}
          />
        ) : undefined
      }
    >
      {formattedTime}
    </TimeSlotButton>
  );
};

const TimeSlots = ({
  appointmentsGroupedByDate,
  selectedAppointment,
  timeZone,
  isLoading,
  onAppointmentSelected,
}: TimeSlotsProps) => {
  const appointmentsMatrix = Object.values(appointmentsGroupedByDate);
  return (
    <StyledGrid cols={appointmentsMatrix.length}>
      {appointmentsMatrix.map((appointments) => (
        <TimeSlotsColumn key={`column-${appointments[0].startTime}`}>
          {appointments.map((appointment) => (
            <SingleTimeSlot
              key={appointment.startTime}
              appointment={appointment}
              selectedAppointment={selectedAppointment}
              timeZone={timeZone}
              isLoading={isLoading}
              onAppointmentSelected={onAppointmentSelected}
            />
          ))}
        </TimeSlotsColumn>
      ))}
    </StyledGrid>
  );
};

const TimeScheduler = ({
  appointments,
  selectedAppointment,
  disabledFutureNavigation,
  disabledPastNavigation,
  maxDatesPerPage,
  timeZone,
  isLoading,
  onAppointmentSelected,
  onFetchFutureDates,
  onFetchPastDates,
}: TimeSchedulerProps) => {
  const [page, setPage] = useState(1);

  const appointmentsGroupedByDate = isLoading
    ? dummyLoadingData
    : groupByDate(appointments);
  const screenSizes = useScreenSizes();

  useEffect(() => {
    setPage(1);
  }, [screenSizes]);

  const paginateAppointments = useCallback(() => {
    if (maxDatesPerPage) {
      return paginate(appointmentsGroupedByDate, page, maxDatesPerPage);
    }

    const { desktop, tablet } = screenSizes;
    let maxItemsPerPage = 1;
    if (tablet) {
      maxItemsPerPage = 3;
    }

    if (desktop) {
      maxItemsPerPage = 5;
    }

    return paginate(appointmentsGroupedByDate, page, maxItemsPerPage);
  }, [appointmentsGroupedByDate, screenSizes, page, maxDatesPerPage]);

  const { maxPages, paginatedAppointments, paginatedDates } =
    paginateAppointments();

  const onFutureDates = () => {
    if (page < maxPages) {
      return setPage(page + 1);
    }

    setPage(1);
    const lastDate = paginatedDates[paginatedDates.length - 1];
    return onFetchFutureDates(lastDate);
  };

  const onPastDates = () => {
    if (page > 1) {
      return setPage(page - 1);
    }

    setPage(1);
    const firstDate = paginatedDates[0];
    return onFetchPastDates(firstDate);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isPastToday = new Date(`${paginatedDates?.[0]}T00:00`) <= today;

  return (
    <>
      <DateHeader
        dates={paginatedDates}
        onPrevious={onPastDates}
        onNext={onFutureDates}
        disabledFutureNavigation={disabledFutureNavigation && page === maxPages}
        disabledPastNavigation={
          disabledPastNavigation && page === 1 && isPastToday
        }
        isLoading={isLoading}
      />
      <TimeSlots
        appointmentsGroupedByDate={paginatedAppointments}
        selectedAppointment={selectedAppointment}
        onAppointmentSelected={onAppointmentSelected}
        timeZone={timeZone}
        isLoading={isLoading}
      />
    </>
  );
};

type GroupedAppointments = {
  [x: string]: Appointment[];
};

type Appointment = {
  startTime: string;
  endTime: string;
  available?: boolean;
};

type SingleTimeSlotProps = {
  appointment: Appointment;
  selectedAppointment?: Appointment;
  timeZone?: string;
  testId?: string;
  isLoading?: boolean;
  onAppointmentSelected: (appointment: Appointment) => void;
};

type TimeSlotsProps = {
  appointmentsGroupedByDate: GroupedAppointments;
  selectedAppointment?: Appointment;
  timeZone?: string;
  isLoading?: boolean;
  onAppointmentSelected: (appointment: Appointment) => void;
};

type TimeSchedulerProps = {
  appointments: Appointment[];
  selectedAppointment?: Appointment;
  disabledPastNavigation?: boolean;
  disabledFutureNavigation?: boolean;
  maxDatesPerPage?: number;
  timeZone?: string;
  isLoading?: boolean;
  onAppointmentSelected: (appointment: Appointment) => void;
  onFetchPastDates: (date: string) => void;
  onFetchFutureDates: (date: string) => void;
};

const TimeSlotsColumn = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  gap: 16px;
`;

const TimeSlotButton = styled(Button)`
  font-weight: 400;
  border-radius: 8px;
  border: 0.5px solid ${tokens.OFFWHITE_40};
  &.sr-button-md.sr-button-secondary {
    padding: 16px;
  }

  &.sr-button-md.sr-button-primary {
    padding: 16px;
  }

  &.sr-button-md.sr-button-secondary:hover {
    background: rgba(31, 38, 71, 0.1);
  }
`;

const StyledGrid = styled.div<{ cols: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.cols}, minmax(0, 1fr));
  gap: 16px;
`;

const StyledSkeleton = styled(Skeleton)`
  width: 100%;
  align-self: center;
  height: 54px;
`;

export type { TimeSchedulerProps };
export { TimeScheduler };
